import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { AlbumSpread } from '../../types';
import { bookViews, spreadNames, spreadWidth, type BookFace, type BookView } from '../../utils/albumSpreads';
import { ChevronLeft, ChevronRight, Icon, X } from '../ui/Icon';
import AlbumSpreadView from './AlbumSpreadView';

type ViewProps = Pick<ComponentProps<typeof AlbumSpreadView>, 'catalogue' | 'photosById' | 'summary' | 'maps' | 'trip' | 'styles' | 'albumStyle'>;

interface Props extends ViewProps {
  spreads: AlbumSpread[];
  onClose: () => void;
}

type Dir = 1 | -1;

const TURN_MS = 750;
/** How far a drag goes, in page widths, before the leaf lies flat on the other side. */
const DRAG_PAGES = 1;
const ease = (k: number) => (k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2);
const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
/** A closed book lies centred: the front cover's page in the middle, not the right half. */
const shiftOf = (v: BookView | undefined) => (!v ? 0 : !v.left ? -25 : !v.right ? 25 : 0);

/**
 * Leafing through the album as a book: the front cover, each spread open, the
 * back cover. A page turns on the spine (CSS 3D) by click, arrow keys or drag.
 * The pages are the editor's own (`AlbumSpreadView compact`): what prints.
 */
export default function AlbumBook({ spreads, onClose, catalogue, photosById, summary, maps, trip, styles, albumStyle }: Props) {
  const { t } = useTranslation();
  const view = useMemo(
    () => ({ catalogue, photosById, summary, maps, trip, styles, albumStyle }),
    [catalogue, photosById, summary, maps, trip, styles, albumStyle],
  );
  const views = useMemo(() => bookViews(catalogue, spreads), [catalogue, spreads]);
  const names = useMemo(() => spreadNames(spreads), [spreads]);

  const [index, setIndex] = useState(0);
  const [turn, setTurn] = useState<Dir | null>(null);
  // The animation reads these between renders; the state above only says what to mount.
  const indexRef = useRef(0);
  const turnRef = useRef<Dir | null>(null);
  const progress = useRef(0);
  const frame = useRef<number | null>(null);
  const drag = useRef<{ startX: number; dir: Dir; width: number; moved: boolean } | null>(null);
  const bookRef = useRef<HTMLDivElement>(null);
  const leafRef = useRef<HTMLDivElement>(null);
  const frontShade = useRef<HTMLDivElement>(null);
  const backShade = useRef<HTMLDivElement>(null);
  const underShade = useRef<HTMLDivElement>(null);

  const paint = useCallback(() => {
    const p = progress.current;
    const dir = turnRef.current;
    const from = views[indexRef.current];
    const to = dir ? views[indexRef.current + dir] : undefined;
    const shift = shiftOf(from) + (to ? (shiftOf(to) - shiftOf(from)) * p : 0);
    if (bookRef.current) bookRef.current.style.transform = `translateX(${shift}%)`;
    if (leafRef.current && dir) leafRef.current.style.transform = `rotateY(${dir === 1 ? -180 * p : 180 * p}deg)`;
    if (frontShade.current) frontShade.current.style.opacity = String(p * 0.45);
    if (backShade.current) backShade.current.style.opacity = String((1 - p) * 0.45);
    if (underShade.current) underShade.current.style.opacity = String((1 - p) * 0.35);
  }, [views]);

  useLayoutEffect(paint, [paint, index, turn]);

  const finish = useCallback((target: 0 | 1) => {
    const dir = turnRef.current;
    if (target === 1 && dir) {
      indexRef.current += dir;
      setIndex(indexRef.current);
    }
    turnRef.current = null;
    progress.current = 0;
    setTurn(null);
  }, []);

  const animate = useCallback((target: 0 | 1) => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    const start = progress.current;
    const ms = reducedMotion() ? 0 : TURN_MS * Math.abs(target - start);
    const t0 = performance.now();
    const step = (now: number) => {
      const k = ms ? Math.min(1, (now - t0) / ms) : 1;
      progress.current = start + (target - start) * ease(k);
      paint();
      if (k < 1) frame.current = requestAnimationFrame(step);
      else { frame.current = null; finish(target); }
    };
    frame.current = requestAnimationFrame(step);
  }, [finish, paint]);

  const begin = useCallback((dir: Dir) => {
    const next = indexRef.current + dir;
    if (turnRef.current || next < 0 || next >= views.length) return false;
    turnRef.current = dir;
    progress.current = 0;
    setTurn(dir);
    return true;
  }, [views.length]);

  const go = useCallback((dir: Dir) => { if (begin(dir)) animate(1); }, [animate, begin]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [go, onClose]);

  useEffect(() => () => { if (frame.current !== null) cancelAnimationFrame(frame.current); }, []);

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    if (turnRef.current || !bookRef.current) return;
    const rect = bookRef.current.getBoundingClientRect();
    drag.current = { startX: e.clientX, dir: e.clientX >= rect.left + rect.width / 2 ? 1 : -1, width: rect.width / 2, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    if (!d.moved) {
      if (Math.abs(dx) < 6) return;
      if (!begin(d.dir)) { drag.current = null; return; }
      d.moved = true;
    }
    progress.current = Math.min(1, Math.max(0, (-dx * d.dir) / (d.width * DRAG_PAGES)));
    paint();
  }

  function onPointerUp() {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) go(d.dir);
    else animate(progress.current > 0.3 ? 1 : 0);
  }

  const current = views[index];
  const target = turn ? views[index + turn] : undefined;
  if (!current) return null;
  const left = turn === -1 && target ? target.left : current.left;
  const right = turn === 1 && target ? target.right : current.right;
  const leaf = turn && target
    ? turn === 1 ? { front: current.right, back: target.left } : { front: current.left, back: target.right }
    : null;

  const W = catalogue.page.width;
  const H = catalogue.page.height;
  const name = (v: BookView) => {
    if (!v.left) return t('album.spread.front');
    if (!v.right) return t('album.spread.back');
    const n = names.get(v.left.spread.id);
    if (!n || n.kind !== 'pages') return '';
    return n.from === n.to ? t('album.page', { number: n.from }) : t('album.spread.pages', { pages: `${n.from}–${n.to}` });
  };
  const navBtn = 'btn-icon w-10 h-10 bg-white/10 hover:bg-white/20 text-white border-0 disabled:opacity-30';

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={t('album.preview.title')} className="fixed inset-0 z-50 flex flex-col bg-slate-950 animate-fade-in">
      <div className="flex justify-end p-3">
        <button type="button" onClick={onClose} className={navBtn} aria-label={t('common.close')} autoFocus>
          <Icon as={X} size={18} />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center px-4 overflow-hidden">
        <div
          ref={bookRef}
          className="relative select-none touch-none cursor-pointer"
          style={{
            width: `min(92vw, calc((100dvh - 9rem) * ${(2 * W) / H}))`,
            aspectRatio: `${2 * W} / ${H}`,
            perspective: '2800px',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDragStart={e => e.preventDefault()}
        >
          <Half side="left">
            <Face face={left} view={view} />
            {turn === -1 && <div ref={underShade} className="pointer-events-none absolute inset-0 bg-gradient-to-l from-black to-transparent" />}
          </Half>
          <Half side="right">
            <Face face={right} view={view} />
            {turn === 1 && <div ref={underShade} className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black to-transparent" />}
          </Half>

          {leaf && turn && (
            <div
              ref={leafRef}
              className="pointer-events-none absolute top-0 h-full w-1/2 z-10"
              style={{ left: turn === 1 ? '50%' : 0, transformOrigin: turn === 1 ? 'left center' : 'right center', transformStyle: 'preserve-3d' }}
            >
              <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}>
                <Face face={leaf.front} view={view} />
                <div ref={frontShade} className={`absolute inset-0 ${turn === 1 ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-black/80 to-transparent`} />
              </div>
              <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                <Face face={leaf.back} view={view} />
                <div ref={backShade} className={`absolute inset-0 ${turn === 1 ? 'bg-gradient-to-r' : 'bg-gradient-to-l'} from-black/80 to-transparent`} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 p-4 text-white">
        <button type="button" onClick={() => go(-1)} disabled={index === 0} className={navBtn} aria-label={t('album.preview.prev')}>
          <Icon as={ChevronLeft} size={20} />
        </button>
        <span className="min-w-[9rem] text-center text-sm text-white/80" aria-live="polite">{name(current)}</span>
        <button type="button" onClick={() => go(1)} disabled={index === views.length - 1} className={navBtn} aria-label={t('album.preview.next')}>
          <Icon as={ChevronRight} size={20} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

function Half({ side, children }: { side: 'left' | 'right'; children: ReactNode }) {
  return <div className={`absolute top-0 h-full w-1/2 ${side === 'left' ? 'left-0' : 'left-1/2'}`}>{children}</div>;
}

/** One page of the book: its spread drawn whole, cut to the page at `x`. Nothing on the table side. */
const Face = memo(function Face({ face, view }: { face: BookFace | null; view: ViewProps }) {
  if (!face) return null;
  const W = view.catalogue.page.width;
  const total = spreadWidth(view.catalogue, face.spread.kind);
  return (
    <div className="absolute inset-0 overflow-hidden bg-white shadow-2xl">
      <div className="absolute top-0 h-full" style={{ width: `${(total / W) * 100}%`, left: `${(-face.x / W) * 100}%` }}>
        <AlbumSpreadView {...view} spread={face.spread} compact />
      </div>
    </div>
  );
});
